import { collection, doc, getDoc, addDoc, updateDoc, deleteDoc, getDocs, QueryConstraint, query, getCountFromServer, setDoc, QueryCompositeFilterConstraint, Query, writeBatch, limit, QueryDocumentSnapshot, DocumentData, startAfter, getFirestore } from "firebase/firestore";
import { getStorage, ref, deleteObject, uploadBytes, getDownloadURL, getBlob } from "firebase/storage";
import { getArrayChunk, sleep } from "../utils/functions";

const storage = getStorage();
const db = getFirestore();

export const getCollection = (path: string, queryConstraint: QueryConstraint[] | QueryCompositeFilterConstraint) => {
  let q: Query;
  const col = collection(db, path);

  if (Array.isArray(queryConstraint)) {
    q = query(col, ...queryConstraint);
  } else {
    q = query(col, queryConstraint);
  }

  return getDocs(q);
};

export const getGenericCollection = async<T extends { id: string; }>(path: string, _query: QueryConstraint[] | QueryCompositeFilterConstraint) => {
  try {
    const { docs } = await getCollection(path, _query);

    return docs.map(doc => ({ ...doc.data(), id: doc.id })) as T[];
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getDocById = (collection: string, id: string) => getDoc(doc(db, collection, id));

export const add = (collectionName: string, data: any) => addDoc(collection(db, collectionName), data);

export const bulkAdd = async (path: string, data: Record<string, any>[]) => {
  const dataChunks = getArrayChunk(data, 500);

  try {
    for (const chunk of dataChunks) {
      const batch = writeBatch(db);

      chunk.forEach((item) => {
        const docRef = doc(collection(db, path));
        batch.set(docRef, item);
      });

      await batch.commit();
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const update = (collection: string, id: string, data: any) => updateDoc(doc(db, collection, id), data);

export const updateByFilters = async (path: string, query: QueryConstraint[], data: any) => {
  const _query = [...query, limit(500)];
  let lastDoc: QueryDocumentSnapshot<DocumentData> | undefined = undefined;

  try {
    while (true) {
      const { docs, empty, size } = await getCollection(path, lastDoc ? [..._query, startAfter(lastDoc)] : _query);

      if (empty) {
        break;
      }

      const batch = writeBatch(db);

      docs.forEach((document) => {
        batch.update(doc(db, path, document.id), data);
      });

      await batch.commit();

      lastDoc = docs[docs.length - 1];

      if (size < 500) {
        break;
      }

      await sleep(1000);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const set = (collection: string, id: string, data: any) => setDoc(doc(db, collection, id), data, { merge: true });

export const del = (collection: string, id: string) => deleteDoc(doc(db, collection, id));

export const bulkDelete = async (path: string, ids: string[]) => {
  const idChunks = getArrayChunk(ids, 500);

  try {
    for (const chunk of idChunks) {
      const batch = writeBatch(db);

      chunk.forEach((id) => {
        const docRef = doc(db, path, id);
        batch.delete(docRef);
      });

      await batch.commit();

      await sleep(1000);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const deleteFile = (url: string) => deleteObject(ref(storage, url));

export const getBlobByUrl = (url: string) => getBlob(ref(storage, url));

export const uploadFile = async (path: string, file: File) => {
  try {
    const url = path + "/" + new Date().toString() + " - " + file.name;
    const storageRef = ref(storage, url);

    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  } catch (error) {
    console.log(error);
    return "";
  }
};

export const getGenericDocById = async <T extends { id: string; }>(collectionName: string, id: string) => {
  try {
    const document = await getDoc(doc(db, collectionName, id));

    if (document.exists()) {
      return { id: document.id, ...document.data() } as T;
    }

    return null;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getCollectionGeneric = async <T extends { id?: string; }>(path: string, _query: QueryConstraint[]) => {
  try {
    const { docs } = await getDocs(query(collection(db, path), ..._query));

    return docs.map(d => ({
      id: d.id,
      ...d.data()
    })) as T[];
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getCollectionCount = async (path: string, _query: QueryConstraint[]) => {
  try {
    const res = await getCountFromServer(query(collection(db, path), ..._query));

    return res.data().count;
  } catch (error) {
    console.log(error);
    throw error;
  }
};